import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, Check, Clock3, Keyboard, MapPin, RefreshCw, ScanLine, Settings, Wifi, WifiOff, X } from "lucide-react";
import { getPublicBootstrap } from "../api/client";
import { Button, IconButton, cx } from "../components/ui";
import { ReaderQueue, sendReaderPunch, type PunchRequest, type PunchAcknowledgement } from "../reader-queue";
import { cacheReaderConfig, readReaderConfig } from "../reader-config";

export function captureReaderPhoto(video: HTMLVideoElement | null): string | undefined {
  if (!video || video.readyState < 2 || !video.videoWidth || !video.videoHeight) return undefined;
  const canvas = document.createElement("canvas");
  canvas.width = Math.min(video.videoWidth, 1280);
  canvas.height = Math.round(video.videoHeight * canvas.width / video.videoWidth);
  const context = canvas.getContext("2d");
  if (!context) return undefined;
  context.drawImage(video, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/jpeg", 0.72);
}

async function coordinates(): Promise<{ latitude?: number; longitude?: number; accuracyMeters?: number }> {
  if (!navigator.geolocation) return {};
  return new Promise((resolve) => navigator.geolocation.getCurrentPosition(
    (position) => resolve({ latitude: position.coords.latitude, longitude: position.coords.longitude, accuracyMeters: position.coords.accuracy }),
    () => resolve({}),
    { enableHighAccuracy: true, maximumAge: 30_000, timeout: 8_000 }
  ));
}

export function ReaderPage({ mode }: { mode: "reader" | "kiosk" }) {
  const [storage] = useState(() => ({
    getItem: (key) => window.localStorage.getItem(key),
    setItem: (key, value) => window.localStorage.setItem(key, value)
  }) as Pick<Storage, "getItem" | "setItem">);
  const [queue] = useState(() => new ReaderQueue(storage, sendReaderPunch));
  const [config, setConfig] = useState(() => readReaderConfig(storage));
  const [configSource, setConfigSource] = useState<"server" | "cache" | "missing">(config ? "cache" : "missing");
  const [initialQueue] = useState(() => {
    try { return { count: queue.read().length, error: "" }; }
    catch (error) { return { count: 0, error: error instanceof Error ? error.message : "Nie można odczytać kolejki." }; }
  });
  const [now, setNow] = useState(new Date());
  const [online, setOnline] = useState(navigator.onLine);
  const [queueCount, setQueueCount] = useState(initialQueue.count);
  const [scanning, setScanning] = useState(false);
  const [manual, setManual] = useState(false);
  const [manualValue, setManualValue] = useState("");
  const [pin, setPin] = useState("");
  const [message, setMessage] = useState(initialQueue.error || "Zbliż kartę QR do kamery");
  const [tone, setTone] = useState<"ready" | "success" | "error" | "offline">(initialQueue.error ? "error" : "ready");
  const [result, setResult] = useState<PunchAcknowledgement | null>(null);
  const deviceId = config?.deviceId;
  const locationId = config?.locationId;
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const detectorTimer = useRef<number | null>(null);
  const busyRef = useRef(false);
  const lastCodeRef = useRef("");

  const refreshConfig = useCallback(async () => {
    try {
      const fresh = cacheReaderConfig(storage, await getPublicBootstrap());
      setConfig(fresh);
      setConfigSource("server");
    } catch (error) {
      const cached = readReaderConfig(storage);
      setConfig(cached);
      setConfigSource(cached ? "cache" : "missing");
      if (!cached) {
        setTone("error");
        setMessage(error instanceof Error ? `${error.message} Brak zapisanej konfiguracji czytnika.` : "Brak zapisanej konfiguracji czytnika. Odzyskaj połączenie.");
      }
    }
  }, [storage]);

  useEffect(() => { void refreshConfig(); }, [refreshConfig]);

  const flush = useCallback(async () => {
    if (!navigator.onLine) return;
    try {
      const report = await queue.flush();
      const remaining = queue.read();
      setQueueCount(remaining.length);
      const rejected = remaining.find((item) => item.delivery?.state === "rejected");
      if (rejected) {
        setTone("error");
        setMessage(`Odrzucone odbicia: ${report.rejected}. ${rejected.delivery!.message} Dane zachowane. Skontaktuj się z administratorem.`);
      } else if (report.pending) {
        setTone("offline");
        setMessage(`Odbicia oczekujące na potwierdzenie: ${report.pending}. Dane zachowane; ponowimy synchronizację.`);
      } else if (report.acknowledged.size > 0) {
        setTone("success");
        setMessage(`Odbicia zsynchronizowane. Potwierdzenia serwera: ${report.acknowledged.size}.`);
      }
      return report;
    } catch (error) {
      setTone("error");
      setMessage(error instanceof Error ? error.message : "Nie można zsynchronizować zapisanych odbić.");
    }
  }, [queue]);

  useEffect(() => {
    const clock = window.setInterval(() => setNow(new Date()), 1000);
    const handleOnline = () => { setOnline(true); void refreshConfig(); void flush(); };
    const handleOffline = () => { setOnline(false); setTone("offline"); };
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    void flush();
    const retry = window.setInterval(() => void flush(), 15_000);
    return () => { clearInterval(clock); clearInterval(retry); window.removeEventListener("online", handleOnline); window.removeEventListener("offline", handleOffline); };
  }, [flush, refreshConfig]);

  const stopCamera = useCallback(() => {
    if (detectorTimer.current) window.clearInterval(detectorTimer.current);
    detectorTimer.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setScanning(false);
  }, []);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const punch = useCallback(async (payload: string) => {
    if (busyRef.current || !payload.trim()) return;
    busyRef.current = true;
    try {
      if (!config || !deviceId) throw new Error("Brak zapisanej konfiguracji czytnika. Połącz się z serwerem przed pierwszym odbiciem.");
      const occurredAt = new Date().toISOString();
      const photoDataUrl = streamRef.current?.active ? captureReaderPhoto(videoRef.current) : undefined;
      if (config.requirePhoto && !photoDataUrl) throw new Error("To odbicie wymaga zdjęcia. Uruchom kamerę i poczekaj na obraz; bez zdjęcia odbicie nie zostanie wysłane.");
      const geo = await coordinates();
      const item: PunchRequest = {
        payload: payload.trim(), pin: pin || undefined, deviceId, locationId,
        occurredAt, idempotencyKey: crypto.randomUUID(), ...geo,
        ...(photoDataUrl ? { photoDataUrl } : {})
      };
      await queue.enqueue(item);
      setQueueCount(queue.read().length);
      setResult(null);
      setTone("offline");
      setMessage("Odbicie zapisane. Oczekuje na potwierdzenie serwera.");
      setManual(false);
      setManualValue("");
      const report = await flush();
      const response = report?.acknowledged.get(item.idempotencyKey);
      if (!response) return;
      setResult(response);
      setTone("success");
      setMessage(response.duplicate ? "Odbicie było już zsynchronizowane" : response.event?.type === "WORK_STOP" ? "Praca zakończona" : "Praca rozpoczęta");
    } catch (error) {
      setTone("error");
      setMessage(error instanceof Error ? error.message : "Nie udało się zapisać odbicia.");
    } finally {
      window.setTimeout(() => { busyRef.current = false; lastCodeRef.current = ""; }, 3500);
    }
  }, [deviceId, locationId, pin, config, queue, flush]);

  const startCamera = async () => {
    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false });
      if (videoRef.current) { videoRef.current.srcObject = streamRef.current; await videoRef.current.play(); }
      setScanning(true);
      setTone("ready");
      const Detector = (window as any).BarcodeDetector;
      if (!Detector) { setMessage("Ta przeglądarka wymaga ręcznego wprowadzenia kodu"); return; }
      const detector = new Detector({ formats: ["qr_code"] });
      detectorTimer.current = window.setInterval(async () => {
        if (!videoRef.current || busyRef.current || videoRef.current.readyState < 2) return;
        try {
          const codes = await detector.detect(videoRef.current);
          const value = codes[0]?.rawValue;
          if (value && value !== lastCodeRef.current) { lastCodeRef.current = value; await punch(value); }
        } catch { /* Camera frames can be transient while Android changes focus. */ }
      }, 350);
    } catch {
      setTone("error");
      setMessage("Nie udało się uruchomić kamery");
    }
  };

  const time = new Intl.DateTimeFormat("pl-PL", { hour: "2-digit", minute: "2-digit", second: "2-digit" }).format(now);
  const date = new Intl.DateTimeFormat("pl-PL", { weekday: "long", day: "numeric", month: "long" }).format(now);

  return (
    <main className={cx("reader-page", `reader-page--${tone}`)}>
      <header className="reader-header"><div className="reader-brand"><span className="brand-symbol"><span>A</span></span><div><strong>Mój ADIKAM</strong><span>{mode === "kiosk" ? "Tryb kiosku" : "Czytnik kart"}</span></div></div><div className="reader-status">{online ? <Wifi size={18} /> : <WifiOff size={18} />}<span>{online ? "Online" : "Offline"}</span>{queueCount ? <BadgeCounter count={queueCount} /> : null}<IconButton label="Ustawienia urządzenia"><Settings size={19} /></IconButton></div></header>
      <section className="reader-content">
        <div className="reader-clock"><strong>{time}</strong><span>{date}</span></div>
        <div className={cx("scanner-frame", scanning && "is-active", result && "has-result")}>
          <video ref={videoRef} muted playsInline />
          {!scanning ? <div className="scanner-placeholder"><ScanLine size={64} strokeWidth={1.4} /><span>Kamera jest gotowa</span></div> : null}
          {scanning ? <><i className="scanner-corner scanner-corner--tl" /><i className="scanner-corner scanner-corner--tr" /><i className="scanner-corner scanner-corner--bl" /><i className="scanner-corner scanner-corner--br" /><span className="scanner-line" /></> : null}
          {result?.employee ? <div className="reader-result"><span>{result.employee.initials || <Check size={28} />}</span><strong>{result.employee.name}</strong></div> : null}
        </div>
        <div className="reader-message">{tone === "success" ? <Check size={22} /> : tone === "error" ? <X size={22} /> : tone === "offline" ? <WifiOff size={22} /> : <ScanLine size={22} />}<strong>{message}</strong></div>
        <div className="reader-actions">{scanning ? <Button icon={<Camera size={17} />} onClick={stopCamera}>Wyłącz kamerę</Button> : <Button variant="primary" icon={<Camera size={17} />} onClick={() => void startCamera()}>Uruchom skaner</Button>}<Button icon={<Keyboard size={17} />} onClick={() => setManual((value) => !value)}>Wprowadź kod</Button>{queueCount ? <Button icon={<RefreshCw size={17} />} onClick={() => void flush()}>Synchronizuj</Button> : null}</div>
        {manual ? <form className="manual-reader" onSubmit={(event) => { event.preventDefault(); void punch(manualValue); }}><input value={manualValue} onChange={(event) => setManualValue(event.target.value)} placeholder="RCP:pracownik:token" autoFocus /><input value={pin} onChange={(event) => setPin(event.target.value)} placeholder="PIN (opcjonalnie)" inputMode="numeric" /><Button type="submit" variant="primary" disabled={!manualValue}>Zarejestruj</Button></form> : null}
      </section>
      <footer className="reader-footer"><span><MapPin size={15} />{locationId || "Brak lokalizacji"}</span><span><SmartDevice />{deviceId || "Brak konfiguracji"}</span><span><Clock3 size={15} />Czas urządzenia</span>{config ? <span>{configSource === "server" && online ? "Ustawienia z serwera" : "Ustawienia zapisane"}: {new Date(config.savedAt).toLocaleString("pl-PL")}</span> : null}</footer>
    </main>
  );
}

function BadgeCounter({ count }: { count: number }) { return <span className="queue-badge">{count}</span>; }
function SmartDevice() { return <span className="device-dot" aria-hidden="true" />; }
